/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React from "react";
import PropTypes from "prop-types";
import styles from "./Table.module.css";
import { Menu, MenuItem } from "@mui/material";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import Moment from "moment";

export const TableRow = ({
  status,
  isDeleted,
  lastModified,
  handlePopupFromTableRow,
  handleRowClick,
  handleAppDetailsClick,
  isOemMode = false,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteApp = () => {
    handlePopupFromTableRow();
  };

  const handleSubmissionRowClick = () => {
    handleRowClick({ submissionId: status.submissionId });
  };

  const handleAppDetailsRowClick = () => {
    if (handleAppDetailsClick) {
      handleAppDetailsClick({ submissionId: status.submissionId });
    } else {
      handleRowClick({ submissionId: status.submissionId });
    }
  };

  return (
    <tr className={styles.rowCls}>
      <td></td>
      <td></td>
      <td className={styles.versionCls}>
        <button
          type="button"
          onClick={isOemMode ? handleAppDetailsRowClick : handleSubmissionRowClick}
          style={{ cursor: "pointer", background: "none", border: "none", padding: 0, font: "inherit", color: "inherit" }}
        >
          {status.version}
        </button>
      </td>
      {isOemMode && (
        <td className={styles.statusCls}>
          <button
            type="button"
            style={{
              color: "#0476E2",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: 400,
              background: "none",
              border: "none",
              padding: 0,
              font: "inherit",
              textDecoration: "underline",
            }}
            onClick={handleSubmissionRowClick}
          >
            View Current Status
          </button>
        </td>
      )}
      {!isOemMode && !isDeleted && status.status === "DRAFT" && (
        <td className={styles.statusCls}>
          <span className={styles.draftStatusBadge}>Draft</span>
        </td>
      )}
      {!isOemMode && !isDeleted && status.status === "PUBLISHED" && (
        <td className={styles.statusCls}>
          <span className={styles.publishedStatusBadge}>Published</span>
        </td>
      )}
      {!isOemMode && !isDeleted && status.status === "REJECTED_FOR_PRODUCTION" && (
        <td className={styles.statusCls}>
          <span className={styles.delistedStatusBadge}>
            Rejected for Production
          </span>
        </td>
      )}
      {!isOemMode && !isDeleted && status.status === "APPROVED_FOR_PRODUCTION" && (
        <td className={styles.statusCls}>
          <span className={styles.submittedStatusBadge}>
            Approved for Production
          </span>
        </td>
      )}
      {!isOemMode && !isDeleted && status.status === "APPROVED_FOR_BETA" && (
        <td className={styles.statusCls}>
          <span className={styles.submittedStatusBadge}>
            Approved for Beta
          </span>
        </td>
      )}
      {!isOemMode && !isDeleted && status.status === "APPROVED_FOR_STAGING" && (
        <td className={styles.statusCls}>
          <span className={styles.submittedStatusBadge}>
            Approved for Staging
          </span>
        </td>
      )}
      {!isOemMode && !isDeleted && status.status === "IN_REVIEW" && (
        <td className={styles.statusCls}>
          <span className={styles.inReviewStatusBadge}>In Review</span>
        </td>
      )}
      {!isOemMode && !isDeleted && status.status === "GENERATING_DELTA" && (
        <td className={styles.statusCls}>
          <span className={styles.inReviewStatusBadge}>Generating Delta</span>
        </td>
      )}
      {!isOemMode && !isDeleted && status.status === "DELISTED_BY_ADMIN" && (
        <td className={styles.statusCls}>
          <span className={styles.delistedStatusBadge}>Delisted by Admin</span>
        </td>
      )}
      {!isOemMode && !isDeleted && status.status === "DELISTED_BY_SELLER" && (
        <td className={styles.statusCls}>
          <span className={styles.delistedStatusBadge}>Delisted by Seller</span>
        </td>
      )}
      {!isOemMode && !isDeleted && status.status === "SUBMITTED_FOR_PRODUCTION" && (
        <td className={styles.statusCls}>
          <span className={styles.submittedStatusBadge}>
            Submittted for Production
          </span>
        </td>
      )}
      {!isOemMode && !isDeleted && status.status === "SUBMITTED_FOR_STAGING" && (
        <td className={styles.statusCls}>
          <span className={styles.submittedStatusBadge}>
            Submittted for Staging
          </span>
        </td>
      )}
      {!isOemMode && isDeleted && (
        <td className={styles.statusCls}>
          <span className={styles.delistedStatusBadge}>
            Waiting for Delete Approval
          </span>
        </td>
      )}
      {!isOemMode && (
        <td
          className={styles.lastModifiedSubCls}
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          {Moment(lastModified).format("DD MMM YYYY")}
        </td>
      )}

      {!isOemMode && (
        <td className={styles.actionedCls}>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            slotProps={{
              list: {
                "aria-labelledby": "basic-button",
              },
            }}
          >
            <MenuItem onClick={handleDeleteApp}>
              <div className={styles.deleteAppCls}>
                <DeleteForeverOutlinedIcon className={styles.deleteAppIconCls} />
                &nbsp;Delete
              </div>
            </MenuItem>
          </Menu>
        </td>
      )}
    </tr>
  );
};

TableRow.propTypes = {
  status: PropTypes.shape({
    submissionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    version: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  isDeleted: PropTypes.bool,
  lastModified: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handlePopupFromTableRow: PropTypes.func,
  handleRowClick: PropTypes.func.isRequired,
  handleAppDetailsClick: PropTypes.func,
  isOemMode: PropTypes.bool,
};
